import { TFunction } from 'i18next'
import z from 'zod'

import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { VisitorHistoryModel } from '@/models'

import {
  formatDateTime,
  formatPermissions,
  formatPermissionTimeRange,
} from '../visitors-dashboard.utils'
import * as S from './visitors-history.styled'

export const visitorsHistoryFiltersSchema = z.object({
  clientId: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  toDate: z.coerce.number().optional(),
})

export type VisitorsHistoryFilters = z.infer<typeof visitorsHistoryFiltersSchema>

export const visitorsHistoryInitialFilters: VisitorsHistoryFilters = {
  clientId: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  toDate: undefined,
}

export const ADDITIONAL_FILTERS_DROPDOWN_WIDTH = '32.8rem'

export const getHistoryColumns = ({
  t,
  timezone,
}: {
  t: TFunction<['visitors']>
  timezone: string
}): TableColumn<VisitorHistoryModel>[] => [
  {
    id: 'checkIn',
    render: (item) => {
      const { date, time } = formatDateTime(item.date, timezone)
      return (
        <S.DateTimeCell>
          <Typography as="span" size="text-small" color="black800">
            {date}
          </Typography>
          <Typography as="span" size="text-small" color="black800">
            {time}
          </Typography>
        </S.DateTimeCell>
      )
    },
    sortAscLabel: t('visitors:columns.checkInSortAsc'),
    sortDescLabel: t('visitors:columns.checkInSortDesc'),
    sortKey: 'checkIn',
    title: t('visitors:columns.checkIn'),
    width: '16rem',
  },
  {
    id: 'name',
    render: (item) => (
      <S.NameCell>
        <Typography as="span" size="text-small" color="black800" breakLines={1}>
          {item.name}
        </Typography>
        {item.type && item.type.toLowerCase() !== 'guest' && (
          <Typography as="span" size="text-small" color="black800">
            {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
          </Typography>
        )}
      </S.NameCell>
    ),
    sortKey: 'name',
    title: t('visitors:dashboard.columns.guestVendorName'),
    width: '18rem',
  },
  {
    id: 'permissions',
    render: (item) => {
      const timeRange = formatPermissionTimeRange(item, timezone)
      return (
        <S.PermissionsCell>
          <S.PermissionRow>
            <S.RowText>
              <Typography as="span" size="text-small" color="black800">
                {formatPermissions(item, timezone)}
              </Typography>
            </S.RowText>
          </S.PermissionRow>
          {timeRange && (
            <Typography as="span" size="text-small" color="black800">
              {timeRange}
            </Typography>
          )}
        </S.PermissionsCell>
      )
    },
    sortAscLabel: t('visitors:columns.permissionsSortAsc'),
    sortDescLabel: t('visitors:columns.permissionsSortDesc'),
    sortKey: 'permission',
    title: t('visitors:columns.permissions'),
    width: '18rem',
  },
  {
    id: 'residentUnit',
    render: (item) => {
      if (!item.client && !item.unit) return EMPTY_VALUE
      return (
        <S.ResidentCell>
          <Typography as="span" size="text-small" color="black800" breakLines={1}>
            {item.client || EMPTY_VALUE}
          </Typography>
          {(item.unit || item.phone) && (
            <Typography as="span" size="text-small" color="black800">
              {[item.unit, item.phone].filter(Boolean).join(' | ')}
            </Typography>
          )}
        </S.ResidentCell>
      )
    },
    sortKey: 'resident',
    title: t('visitors:columns.residentUnit'),
    width: '18rem',
  },
]
